import * as React from 'react';
import { useState, useEffect, Fragment, useRef } from 'react';
import { useInView } from 'react-intersection-observer';
import get from 'lodash/get';
import './styles.scss';

import {
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
  getExpandedRowModel,
} from '@tanstack/react-table';
import { cn } from 'helpers/css';
import {
  DndContext,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { restrictToHorizontalAxis } from '@dnd-kit/modifiers';
import { arrayMove, SortableContext, horizontalListSortingStrategy } from '@dnd-kit/sortable';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from 'shadcn/components/table';
import { Skeleton } from 'shadcn/components/skeleton';
import DialogConfigOrderingColumns from 'components/Dialogs/DialogConfigOrderingColumns';
import useToggleDialog from 'hooks/useToggleDialog';
import { CommonIcons } from 'components/Icons';
import NewDesign from 'components/NewDesign';
import { TooltipReactTooltip } from 'components/Tooltip';
import { IsJsonString } from 'helpers';
import { isArray, isEmpty, isString } from 'lodash';
import { notifySuccess } from 'helpers/handleNotification';
import { useTranslation } from 'react-i18next';
import { sortingFns } from '@tanstack/react-table';
import { replaceAllCharTo } from 'helpers/string';

const customSortFns = {
  ...sortingFns,
  monitoringStats: (rowA, rowB, columnId) => {
    const compare = (Astat, Bstat) => {
      console.log(`👉 sort: a: ${Astat} - b: ${Bstat}`);
      if (isString(Astat)) {
        const charsNeedRemove = ['$', '%', ',', 'sec', 's', 'block'];
        const AstatFormatted = replaceAllCharTo(Astat, charsNeedRemove, '');
        const BstatFormatted = replaceAllCharTo(Bstat, charsNeedRemove, '');
        return parseFloat(AstatFormatted) - parseFloat(BstatFormatted);
      }

      return (Astat || 0) - (Bstat || 0);
    };

    if (columnId.includes('operation')) {
      const [_, operation] = columnId.split(':');

      const mathOperations = ['+', '-', '*', '/'];
      if (mathOperations.some((el) => operation.includes(el))) {
        let tempFieldsStr = operation;
        mathOperations.forEach((operator) => {
          tempFieldsStr = tempFieldsStr.replace(operator, '-');
        });

        const fieldsSeparated = tempFieldsStr.split('-');

        //* 1.
        //* Astat (Bstat) is a string as "delivered/sent"
        let Astat = operation;
        let Bstat = operation;

        fieldsSeparated.forEach((field) => {
          const valueOfA = get(rowA, `original.${field}`);
          const valueOfB = get(rowB, `original.${field}`);

          Astat = Astat.replace(field, valueOfA);
          Bstat = Bstat.replace(field, valueOfB);
        });

        //* 2.
        //* Astat (Bstat) will be filled value of row by field like: 30/615 , 40/615
        return compare(eval(Astat), eval(Bstat));
      }
    }

    const Astat = rowA.getValue(columnId);
    const Bstat = rowB.getValue(columnId);
    return compare(Astat, Bstat);
  },
};

const getCommonPinningStyles = (column) => {
  const isPinned = column.getIsPinned();
  const isLastLeftPinnedColumn = isPinned === 'left' && column.getIsLastColumn('left');
  const isFirstRightPinnedColumn = isPinned === 'right' && column.getIsFirstColumn('right');

  if (!isPinned) return {};

  return {
    // boxShadow: isLastLeftPinnedColumn
    //   ? '-4px 0 4px -4px gray inset'
    //   : isFirstRightPinnedColumn
    //     ? '4px 0 4px -4px gray inset'
    //     : undefined,
    left: isPinned === 'left' ? `${column.getStart('left')}px` : undefined,
    right: isPinned === 'right' ? `${column.getAfter('right')}px` : undefined,
    // opacity: isPinned ? 0.95 : 1,
    position: isPinned ? 'sticky' : 'relative',
    width: `${column.getSize()} !important`,
    zIndex: isPinned ? 20 : 0,
    // backgroundColor: 'white',
  };
};

const pickColumnVisibility = (columns = [{ label: '', value: '', isHide: false }]) => {
  const columnVisibility = {};
  columns.forEach((col) => {
    columnVisibility[col.value] = !col.isHide;
  });
  return columnVisibility;
};

const checkIfPersistAvailable = (columnsPersisted) => {
  return (
    columnsPersisted &&
    IsJsonString(columnsPersisted) &&
    isArray(JSON.parse(columnsPersisted)) &&
    !isEmpty(JSON.parse(columnsPersisted))
  );
};

const DragAlongCell = ({ cell }) => {
  const { isDragging, setNodeRef, transform } = useSortable({
    id: cell.column.id,
  });

  const draggingStyle = {
    opacity: isDragging ? 0.8 : 1,
    position: 'relative',
    transform: CSS.Translate.toString(transform),
    transition: 'width transform 0.2s ease-in-out',
    width: `${cell.column.getSize()} !important`,
    zIndex: isDragging ? 1 : 0,
  };

  return (
    <TableCell
      ref={setNodeRef}
      key={cell.id}
      // style={cell.column.getIsPinned() ? { ...getCommonPinningStyles(cell.column) } : draggingStyle}
      style={Object.assign(
        {},
        cell.column.getIsPinned() ? { ...getCommonPinningStyles(cell.column) } : draggingStyle
      )}
      className={cn(
        'tw-overflow-hidden',
        cell.column.getIsPinned() && 'tw-bg-white dark:tw-bg-[var(--bg-dark)]'
      )}
    >
      {flexRender(cell.column.columnDef.cell, cell.getContext())}
    </TableCell>
  );
};

const ButtonOrderConfig = ({ columns, onSubmit }) => {
  const [openOrderColumn, toggleOrderColumn, shouldRenderOrderColumn] = useToggleDialog();

  return (
    <>
      <TooltipReactTooltip messageToolTip='Column Setting'>
        <NewDesign.Button
          variant='ghost'
          className={'tw-w-10 tw-h-10 tw-rounded-full'}
          onClick={toggleOrderColumn}
        >
          <CommonIcons.Settings className='tw-text-primary tw-icon' />
        </NewDesign.Button>
      </TooltipReactTooltip>

      {shouldRenderOrderColumn && (
        <DialogConfigOrderingColumns
          isOpen={openOrderColumn}
          toggle={toggleOrderColumn}
          columns={columns}
          onSubmit={(values, formikHelpers) =>
            onSubmit(values, { ...formikHelpers, toggle: toggleOrderColumn })
          }
        />
      )}
    </>
  );
};

/**
 *
 * @param {Object} props
 * @param {{ id: string, accessorKey: string, enableSorting: boolean, header: ({ column }) => JSX | string, cell: (rowProps) => void }[]} props.columns
 * @param {{ data: any, isFetching: boolean, hasMore: boolean, loadingMore: boolean, fetchNextPage: () => void }} props.dataSource
 * @param {string} props.className
 * @param {string} props.tableClassName
 * @param {string} props.toolbarClassName
 * @param {boolean} props.hideColumnView
 * @param {string[]} props.leftPinningColumns - id of column E.g: leftPinningColumns={['id']}
 * @param {string[]} props.rightPinningColumns - id of column E.g: rightPinningColumns={['id']}
 *
 * @param {Object} props.manualControl
 * @param {{ id: string, desc: boolean }[]} props.manualControl.sorting
 * @param {() => void} props.manualControl.setSorting
 * @param {(nextOrderColumns) => void} props.manualControl.setColumnOrder
 *
 * @param {(row) => JSX} props.expandableRowRenderer
 * @param {string} props.rowIdPath
 * @param {({ buttonOrdering }) => any} props.toolbar - buttonOrdering will be enabled when ordering is defined otherwise it will be null
 *
 * @param {Object} props.ordering
 * @param {string} props.ordering.persistKey - Will automatically load from localStorage with the key
 * @param {{ label: '', value: '', isHide: boolean, disableDrag: boolean }[]} props.ordering.columns - In case you load columns ordered from API
 * @param {(values: { columns: any }, formikHelpers: any) => void} props.ordering.onSubmit - onSubmit ordering columns
 * @param {boolean} props.autoSetSizeColumn
 * @returns
 */
export default function DataGrid({
  columns,
  dataSource,
  tableClassName,
  toolbarClassName,
  className,
  leftPinningColumns,
  rightPinningColumns,
  expandableRowRenderer,
  clientSideSorting,
  hideColumnView = true,
  manualControl,
  updateOrderConfig,
  initialColumnVisibility,
  enableColumnResizing = false,
  defaultColumnSize = {
    size: 200,
    minSize: 50,
    maxSize: 900,
  },
  rowIdPath,
  ordering,
  toolbar,
  autoSetSizeColumn = true,
}) {
  //! State
  const { t } = useTranslation();
  const [sorting, setSorting] = useState([]);
  const [expanded, setExpanded] = useState({});
  const [columnPinning, setColumnPinning] = useState({
    left: leftPinningColumns || [],
    right: rightPinningColumns || [],
  });
  const [columnFilters, setColumnFilters] = useState([]);
  const isTableHavePinningColumns = !isEmpty(leftPinningColumns) || !isEmpty(rightPinningColumns);

  //! Ordering
  const [columnOrder, setColumnOrder] = useState(() => {
    if (ordering?.persistKey) {
      const columnsPersisted = localStorage.getItem(ordering.persistKey);
      if (checkIfPersistAvailable(columnsPersisted)) {
        return JSON.parse(columnsPersisted).map((el) => el.value);
      }
    }
    return columns.map((el) => el.id);
  });
  const externalColumnOrder = React.useMemo(() => {
    return !isEmpty(ordering?.columns) ? ordering?.columns.map((el) => el.value) : null;
  }, [ordering]);

  //! Show/hide column
  const [columnVisibility, setColumnVisibility] = useState(() => {
    if (ordering?.persistKey) {
      const columnsPersisted = localStorage.getItem(ordering.persistKey);
      if (checkIfPersistAvailable(columnsPersisted)) {
        return pickColumnVisibility(JSON.parse(columnsPersisted));
      }
    }
    return pickColumnVisibility(columns.map((el) => ({ value: el.id, isHide: false })));
  });
  const externalColumnVisibility = React.useMemo(() => {
    return !isEmpty(ordering?.columns) ? pickColumnVisibility(ordering?.columns) : null;
  }, [ordering]);

  const [columnResizeMode, setColumnResizeMode] = React.useState('onEnd');

  const [columnResizeDirection, setColumnResizeDirection] = React.useState('ltr');
  const [containerWidth, setcontainerWidth] = useState(0);

  const { ref, inView } = useInView();

  const containerRef = useRef(null);

  useEffect(() => {
    if (!containerRef.current) return;
    const elementWidth = containerRef.current.clientWidth;
    setcontainerWidth(elementWidth);
    const resizeObserver = new ResizeObserver((entries) => {
      for (let entry of entries) {
        const { width, height } = entry.contentRect;
        setcontainerWidth(width);
      }
    });

    resizeObserver.observe(containerRef.current);

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  const onSortingChange = React.useCallback(
    (updaterOrValue) => {
      const setSortingFunc = !!manualControl?.setSorting ? manualControl.setSorting : setSorting;

      if (typeof updaterOrValue === 'function') {
        const newSorting = updaterOrValue(sorting);
        setSortingFunc(newSorting);
      } else {
        setSortingFunc(updaterOrValue);
      }
    },
    [sorting, setSorting]
  );

  const {
    status,
    data,
    error,
    isFetching,
    fetchNextPage,
    hasMore,
    loadingMore,
    onSortChange,
    onFilterChange,
  } = dataSource;

  const table = useReactTable(
    Object.assign(
      {},
      clientSideSorting
        ? { getSortedRowModel: getSortedRowModel(), sortingFns: customSortFns }
        : {},
      {
        data,
        columns,
        getCoreRowModel: getCoreRowModel(),
        getExpandedRowModel: getExpandedRowModel(),
        getRowCanExpand: (row) => true,
        getSubRows: (row) => row.subRows,
        manualSorting: !clientSideSorting,

        initialState: {
          columnVisibility: initialColumnVisibility || columnVisibility,
        },

        state: {
          sorting: manualControl?.sorting || sorting,
          expanded: manualControl?.expanded || expanded,
          columnPinning,
          columnFilters,
          columnOrder: externalColumnOrder || columnOrder,
          columnVisibility: externalColumnVisibility || columnVisibility,
          // columnSizing,
        },
        onSortingChange,
        manualFiltering: true,
        onExpandedChange: manualControl?.setExpanded || setExpanded,
        onColumnPinningChange: setColumnPinning,
        onColumnFiltersChange: setColumnFilters,

        // debugTable: true,
        // debugHeaders: true,
        // debugColumns: true,

        defaultColumn: {
          ...defaultColumnSize,
        },
        columnResizeMode,
        columnResizeDirection,
        enableColumnResizing,

        getRowId: (originalRow, index, parent) => {
          const builtByIdPath = get(originalRow, rowIdPath || '');
          return builtByIdPath ? builtByIdPath : index.toString();
        },

        enableHiding: typeof hideColumnView === 'boolean' ? !hideColumnView : undefined,
        enableMultiSort: manualControl?.enableMultiSort || false,
        isMultiSortEvent: manualControl?.enableMultiSort
          ? (e) => e.ctrlKey || e.shiftKey
          : undefined,
      }
    )
  );

  function handleDragEnd(event) {
    const { active, over } = event;
    if (active && over && active.id !== over.id) {
      setColumnOrder((columnOrder) => {
        const oldIndex = columnOrder.indexOf(active.id);
        const newIndex = columnOrder.indexOf(over.id);
        const newOrder = arrayMove(columnOrder, oldIndex, newIndex);
        if (updateOrderConfig) updateOrderConfig(newOrder);
        return newOrder;
      });
    }
  }

  const sensors = useSensors(
    useSensor(MouseSensor, {}),
    useSensor(TouchSensor, {}),
    useSensor(KeyboardSensor, {})
  );

  const { rows } = table.getRowModel();

  useEffect(() => {
    if (inView) {
      if (!isFetching && hasMore && fetchNextPage) fetchNextPage();
    }
  }, [inView]);

  useEffect(() => {
    if (onSortChange) {
      if (table.getState().sorting.length > 0)
        onSortChange(
          table.getState().sorting[0].id,
          table.getState().sorting[0].desc ? 'DESC' : 'ASC'
        );
    }
  }, [table.getState().sorting]);

  //! Render
  const buttonOrdering = React.useMemo(() => {
    if (!ordering) {
      return null;
    }

    return (
      <ButtonOrderConfig
        columns={(externalColumnOrder || columnOrder).map((id) => {
          const columnFound = columns.find((el) => el.id === id);
          return {
            label: columnFound?.meta?.label || '',
            value: columnFound?.id,
            disableDrag: columnFound?.disableDrag,
            isHide: !get(externalColumnVisibility || columnVisibility, id),
          };
        })}
        onSubmit={(values, formikHelpers) => {
          if (ordering.onSubmit) {
            ordering.onSubmit(values, formikHelpers);
            return;
          }

          const nextColumns = values.columns;
          if (ordering?.persistKey) {
            localStorage.setItem(ordering?.persistKey, JSON.stringify(nextColumns));
          }

          setColumnVisibility(() => pickColumnVisibility(nextColumns));
          setColumnOrder(() => nextColumns.map((el) => el.value));
          notifySuccess(t('notice:column_updated'));
          formikHelpers.toggle();
        }}
      />
    );
  }, [
    t,
    ordering,
    columns,
    columnOrder,
    externalColumnOrder,
    externalColumnVisibility,
    columnVisibility,
  ]);

  return (
    <div className={cn(className)}>
      <div className={cn('table-grid-toolbar tw-flex tw-justify-end tw-mb-2', toolbarClassName)}>
        {toolbar
          ? toolbar({
              buttonOrdering: buttonOrdering,
            })
          : buttonOrdering}
      </div>

      <DndContext
        collisionDetection={closestCenter}
        modifiers={[restrictToHorizontalAxis]}
        onDragEnd={handleDragEnd}
        sensors={sensors}
      >
        <div
          className={cn(
            'table-wrapper tw-rounded-md tw-border tw-scrollbar tw-scrollbar-thumb-gray-400 tw-scrollbar-track-transparent',
            tableClassName
          )}
          style={{ overflow: 'auto', overscrollBehaviorX: 'contain' }}
        >
          <div className='tw-w-full tw-h-full' ref={containerRef}>
            <Table
              className='tw-min-w-full tw-w-full'
              {...{
                style: {
                  width: isTableHavePinningColumns ? table.getCenterTotalSize() : undefined,
                },
              }}
            >
              <TableHeader className='tw-sticky tw-top-0 tw-z-30'>
                {table.getHeaderGroups().map((headerGroup) => (
                  <TableRow
                    key={headerGroup.id}
                    className='tw-bg-white dark:tw-bg-[var(--bg-dark)]'
                  >
                    <Fragment>
                      {headerGroup.headers.map((header) => {
                        const { column } = header;
                        const isPinned = column.getIsPinned();

                        return (
                          <TableHead
                            key={header.id}
                            colSpan={header.colSpan}
                            style={{ ...getCommonPinningStyles(column) }}
                            className={cn(
                              isPinned && 'tw-bg-white dark:tw-bg-[var(--bg-dark)]',
                              autoSetSizeColumn ? `tw-w-[${header.column.getSize()}px]` : ''
                            )}
                          >
                            {header.isPlaceholder ? null : (
                              <>{flexRender(header.column.columnDef.header, header.getContext())}</>
                            )}
                          </TableHead>
                        );
                      })}
                    </Fragment>
                  </TableRow>
                ))}
              </TableHeader>
              <TableBody style={{ position: 'relative' }}>
                {isFetching ? (
                  <TableRow key='loading-row'>
                    <TableCell colSpan={columns.length}>
                      <div className='tw-flex tw-flex-col tw-space-y-3'>
                        <Skeleton className='tw-h-4 tw-w-[250px]' />
                        <Skeleton className='tw-h-4 tw-w-[200px]' />
                      </div>
                    </TableCell>
                  </TableRow>
                ) : rows.length !== 0 ? (
                  <>
                    {rows.map((row, index) => {
                      return (
                        <Fragment key={row.id}>
                          <TableRow key={row.id}>
                            {row.getVisibleCells().map((cell) => (
                              <>
                                <SortableContext
                                  key={cell.id}
                                  items={columnOrder}
                                  strategy={horizontalListSortingStrategy}
                                >
                                  <DragAlongCell key={cell.id} cell={cell} />
                                </SortableContext>
                              </>
                            ))}
                          </TableRow>
                          {expandableRowRenderer && row?.getIsExpanded() && (
                            <TableRow
                              key={row.index + '-expandable'}
                              className='row-expanded hover:tw-bg-inherit'
                            >
                              <TableCell colSpan={row.getVisibleCells().length}>
                                <div
                                  className={`row-expanded__cell tw-h-fit tw-sticky tw-left-[10px] tw-bg-muted tw-p-2 dark:tw-bg-[var(--bg-dark)]`}
                                  style={{ width: `${containerWidth - 14}px` }}
                                >
                                  {expandableRowRenderer(row)}
                                </div>
                              </TableCell>
                            </TableRow>
                          )}
                        </Fragment>
                      );
                    })}
                    {hasMore && (
                      <Fragment key={'end-of-table'}>
                        <TableRow>
                          <TableCell colSpan={columns.length}>
                            <div ref={ref} className='tw-space-y-3 tw-w-full'>
                              {loadingMore ? (
                                <Skeleton className='tw-h-4 tw-w-[150px]' />
                              ) : isFetching ? null : (
                                <div className='tw-h-[5px]' />
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      </Fragment>
                    )}
                  </>
                ) : (
                  <Fragment key={'data-not-found'}>
                    <TableRow>
                      <TableCell colSpan={columns.length}>
                        <p>Data Not Found</p>
                      </TableCell>
                    </TableRow>
                  </Fragment>
                )}
              </TableBody>
            </Table>
          </div>
        </div>
      </DndContext>
    </div>
  );
}
